// ════════════════════════════════════════════════════════════════════════
//  Die Lehrgänge einer Klasse
//
//  Eine Klasse führt mehrere, jeden auf genau einer veröffentlichten Fassung.
//  Drei Vorgänge: hinzufügen, entfernen – und der Katalog, aus dem gewählt wird.
//
//  „Fassung aktualisieren“ war einmal der vierte. Er ist entfallen: die Fassung
//  steht fest, sobald das erste Kind geübt hat, und neue Klassen bekommen
//  ohnehin immer die neueste. Was einer laufenden Klasse bleibt, ist entfernen
//  und neu hinzufügen – ein gewarnter Handgriff statt eines Dropdowns, das man
//  im Vorbeigehen benutzt. Die Grenze selbst steht als Trigger in
//  `20260905130000_fassung_friert_mit_dem_ersten_ergebnis_ein.sql`.
// ════════════════════════════════════════════════════════════════════════

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { parseBundle, type Bundle } from "@otto/lehrgang";
import { queryKeys, type OttoClient } from "@otto/plattform";
import type { CourseOption } from "./assign.js";

export type ClassCourse = {
  readonly curriculumId: string;
  readonly slug: string;
  readonly title: string;
  readonly subtitle: string | null;
  /** Der inhaltliche Vorgänger, oder null. Erzwungen wird er nur, wenn die
   *  Klasse es will – siehe `requiresEnforced`. */
  readonly requiresId: string | null;
  readonly versionId: string;
  readonly version: number;
  /** Null, wenn die Fassung nicht lesbar ist – sichtbar machen, nicht verschlucken. */
  readonly bundle: Bundle | null;
  /** Benutzt diese Klasse den Test dieses Lehrgangs? */
  readonly assessmentEnabled: boolean;
  /** Erzwingt sie den Vorgänger für ihre Kinder? */
  readonly requiresEnforced: boolean;
  /** Wie ihre Lektionen freigegeben werden: `frei`, `reihenfolge`, `freigabe`. */
  readonly lessonGate: string;
  /** Sternschwelle der vorigen Lektion, oder null. Nur bei `reihenfolge`. */
  readonly lessonGateStars: number | null;
};

/** Die Lehrgänge der Klasse, in der Reihenfolge, in der sie sie führt. */
export function useClassCourses(client: OttoClient, classId: string) {
  return useQuery({
    queryKey: queryKeys.classCourses(classId),
    enabled: classId !== "",
    queryFn: async (): Promise<ClassCourse[]> => {
      const { data, error } = await client
        .from("class_curricula")
        // Eine Zeichenkette, nicht zusammengesetzt: der Supabase-Client liest den
        // Aufbau aus dem LITERAL. Ein `"a" + "b"` ist für TypeScript nur noch
        // `string`, und alle Feldtypen fallen auf `GenericStringError` zurück.
        .select(
          "position, curriculum_id, assessment_enabled, requires_enforced, lesson_gate, lesson_gate_stars, curricula(slug, title, subtitle, requires_id), curriculum_versions(id, version, content)",
        )
        .eq("class_id", classId)
        .order("position");
      if (error) throw error;

      return (data ?? []).map((row) => {
        const curriculum = row.curricula as {
          slug: string;
          title: string;
          subtitle: string | null;
          requires_id: string | null;
        };
        const version = row.curriculum_versions as {
          id: string;
          version: number;
          content: unknown;
        };
        const checked = parseBundle(version.content);
        return {
          curriculumId: row.curriculum_id,
          slug: curriculum.slug,
          title: curriculum.title,
          subtitle: curriculum.subtitle,
          requiresId: curriculum.requires_id,
          versionId: version.id,
          version: version.version,
          bundle: "bundle" in checked ? checked.bundle : null,
          assessmentEnabled: row.assessment_enabled,
          requiresEnforced: row.requires_enforced,
          lessonGate: row.lesson_gate,
          lessonGateStars: row.lesson_gate_stars,
        };
      });
    },
  });
}

/**
 * Alle Lehrgänge mit ihrer neuesten veröffentlichten Fassung.
 *
 * Ohne `content`: der Katalog dient der Auswahl, und ein Bundle je Lehrgang
 * mitzuschleppen wäre für eine Liste von Namen eine erstaunliche Datenmenge.
 * Die Nummer der neuesten Fassung kommt mit – daran erkennt die Klassenseite,
 * dass eine neuere existiert.
 */
export function useCourseCatalog(client: OttoClient) {
  return useQuery({
    queryKey: queryKeys.courseCatalog,
    queryFn: async (): Promise<CourseOption[]> => {
      const { data, error } = await client
        .from("curricula")
        .select(
          "id, slug, title, subtitle, position, requires_id, retired_at, curriculum_versions(id, version, published_at)",
        )
        .order("position");
      if (error) throw error;

      return (data ?? []).map((row) => {
        // Die Policy liefert ohnehin nur veröffentlichte Fassungen. Der Filter
        // steht trotzdem hier: die Auswahl darf nicht davon abhängen, dass eine
        // Policy anderswo weiterhin genau das tut.
        const published = (
          row.curriculum_versions as {
            id: string;
            version: number;
            published_at: string | null;
          }[]
        )
          .filter((v) => v.published_at !== null)
          .sort((a, b) => b.version - a.version);
        const latest = published[0] ?? null;
        return {
          curriculumId: row.id,
          slug: row.slug,
          title: row.title,
          subtitle: row.subtitle,
          position: row.position,
          requiresId: row.requires_id,
          retiredAt: row.retired_at,
          latestVersionId: latest?.id ?? null,
          latestVersion: latest?.version ?? null,
        };
      });
    },
  });
}

/**
 * Nach jedem der drei Vorgänge dasselbe: die Lehrgänge der Klasse und ihre
 * Ergebnisse sind veraltet. Die Ergebnisse deshalb, weil die Tabelle die Stufen
 * der Fassung zeigt – nicht nur die Zahlen darin.
 */
function invalidateClass(
  queryClient: ReturnType<typeof useQueryClient>,
  classId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.classCourses(classId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.results(classId) });
}

export function useAddCourse(client: OttoClient, classId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      curriculumId: string;
      versionId: string;
      position: number;
    }) => {
      const { error } = await client.from("class_curricula").insert({
        class_id: classId,
        curriculum_id: input.curriculumId,
        curriculum_version_id: input.versionId,
        position: input.position,
      });
      if (error) throw error;
    },
    onSuccess: () => invalidateClass(queryClient, classId),
  });
}

export function useRemoveCourse(client: OttoClient, classId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (curriculumId: string) => {
      const { error } = await client
        .from("class_curricula")
        .delete()
        .eq("class_id", classId)
        .eq("curriculum_id", curriculumId);
      if (error) throw error;
    },
    onSuccess: () => invalidateClass(queryClient, classId),
  });
}

/**
 * Die zwei Schalter der Klasse an einem Lehrgang: Test benutzen, Vorgänger
 * erzwingen.
 *
 * Eine Mutation für beide, kein Vorgang je Haken – sie sitzen nebeneinander,
 * treffen dieselbe Zeile und unterscheiden sich für die Datenbank in nichts.
 * Was sie bedeuten, unterscheidet sich sehr; das steht in der Oberfläche, wo es
 * jemand liest.
 *
 * Geschickt wird nur die geklickte Spalte. Beide zusammen zu schreiben hieße,
 * dass ein Klick auf den einen Haken den Wert des anderen still mitsetzt –
 * gegen einen Stand, der vielleicht schon älter ist als die Seite.
 */
export function useCourseSwitches(client: OttoClient, classId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      curriculumId: string;
      assessmentEnabled?: boolean;
      requiresEnforced?: boolean;
      /** Zusammen geschickt: die Schwelle ergibt ohne die Regel keinen Sinn. */
      lessonGate?: { gate: string; stars: number | null };
    }) => {
      const { error } = await client
        .from("class_curricula")
        .update({
          ...(input.assessmentEnabled === undefined
            ? {}
            : { assessment_enabled: input.assessmentEnabled }),
          ...(input.requiresEnforced === undefined
            ? {}
            : { requires_enforced: input.requiresEnforced }),
          ...(input.lessonGate === undefined
            ? {}
            : {
                lesson_gate: input.lessonGate.gate,
                lesson_gate_stars: input.lessonGate.stars,
              }),
        })
        .eq("class_id", classId)
        .eq("curriculum_id", input.curriculumId);
      if (error) throw error;
    },
    onSuccess: () => invalidateClass(queryClient, classId),
  });
}

/**
 * Hat in dieser Klasse zu diesem Lehrgang schon jemand etwas erreicht?
 *
 * Gefragt sind die Stufen und der Test der Fassung, die die Klasse ÜBT – nicht
 * irgendein Fortschritt des Kindes. Genau danach entscheidet auch der Trigger,
 * ob die Fassung noch wechseln darf; die Oberfläche fragt vorher, damit sie
 * keinen Knopf anbietet, den die Datenbank hinterher ablehnt.
 *
 * `head: true` – gezählt wird, nichts geladen. Die Verwaltung braucht hier keine
 * Zeilen, und Schülerdaten holt man nicht, um sie wegzuwerfen.
 */
export function useCourseHasResults(
  client: OttoClient,
  classId: string,
  course: ClassCourse,
  enabled: boolean,
) {
  const stageKeys = (course.bundle?.lessons ?? []).flatMap((lesson) =>
    lesson.stages.map((stage) => stage.key),
  );
  const testKey = course.bundle?.assessment?.key ?? null;

  return useQuery({
    queryKey: queryKeys.courseResults(classId, course.curriculumId),
    enabled: enabled && classId !== "",
    queryFn: async (): Promise<boolean> => {
      if (stageKeys.length > 0) {
        const { count, error } = await client
          .from("progress")
          .select("stage_key, students!inner(class_id)", { count: "exact", head: true })
          .eq("students.class_id", classId)
          .in("stage_key", stageKeys);
        if (error) throw error;
        if ((count ?? 0) > 0) return true;
      }

      if (testKey !== null) {
        const { count, error } = await client
          .from("test_results")
          .select("test_key, students!inner(class_id)", { count: "exact", head: true })
          .eq("students.class_id", classId)
          .eq("test_key", testKey);
        if (error) throw error;
        if ((count ?? 0) > 0) return true;
      }

      return false;
    },
  });
}

/**
 * Die Klasse auf die neueste veröffentlichte Fassung ziehen.
 *
 * Der Nachfolger des alten Fassungs-Dropdowns, und bewusst enger: es gibt kein
 * Ziel zu wählen. Entweder die Klasse ist auf der neuesten – dann gibt es nichts
 * zu tun – oder sie zieht nach. Eine Klasse auf eine ÄLTERE Fassung zu setzen
 * war nie ein Vorgang, den jemand gebraucht hat.
 *
 * Ob es überhaupt geht, entscheidet die Datenbank: ab dem ersten Ergebnis steht
 * die Fassung fest. Die Oberfläche fragt vorher (`useCourseHasResults`), aber
 * der Fehler bleibt möglich – zwischen Frage und Klick kann ein Kind geübt
 * haben. Er wird angezeigt, nicht verschluckt.
 */
export function useUpdateToLatestVersion(client: OttoClient, classId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { curriculumId: string; versionId: string }) => {
      const { error } = await client
        .from("class_curricula")
        .update({ curriculum_version_id: input.versionId })
        .eq("class_id", classId)
        .eq("curriculum_id", input.curriculumId);
      if (error) throw error;
    },
    onSuccess: () => invalidateClass(queryClient, classId),
  });
}
